/**
 * Global handling for expired / invalid sessions.
 *
 * When any `api/` or `auth/` request returns 401, clear the local session
 * and send the user to login. Concurrent 401s share one in-flight run so
 * we don't stack redirects.
 */

let unauthorizedInFlight = null;

const AUTH_ROUTE_NAMES = new Set([
  'login',
  'signup',
  'forgot-password',
  'reset-password',
  'auth-callback',
]);

/**
 * Resolve the pathname of an axios request config.
 * @param {Object} config
 * @returns {string}
 */
export function getRequestPath(config = {}) {
  const url = config.url || '';
  if (!url) {
    return '';
  }

  const withoutQuery = url.split('?')[0];

  if (/^https?:\/\//i.test(withoutQuery)) {
    try {
      return new URL(withoutQuery).pathname;
    } catch (error) {
      return withoutQuery;
    }
  }

  if (config.baseURL) {
    try {
      return new URL(withoutQuery, config.baseURL).pathname;
    } catch (error) {
      // Fall through to the relative-path handling below.
    }
  }

  return withoutQuery.startsWith('/')
    ? withoutQuery
    : `/${withoutQuery}`;
}

/**
 * True when the request targeted the app's `api/` or `auth/` surface.
 * @param {Object} config
 * @returns {boolean}
 */
export function isApiOrAuthRequest(config) {
  const path = getRequestPath(config);
  return /(?:^|\/)(?:api|auth)(?:\/|$)/.test(path);
}

/**
 * Reset the in-flight lock. Exported for unit tests.
 */
export function resetSessionExpiryHandler() {
  unauthorizedInFlight = null;
}

/**
 * Clear the local session and redirect to login when a 401 comes from
 * `api/` or `auth/`. No-ops for other URLs / status codes, and does not
 * redirect when there was no local session (so public pages that happen
 * to 401 are not forced through login).
 *
 * @param {Object} error - Axios error
 * @returns {Promise<void>}
 */
export async function handleApiUnauthorized(error) {
  if (error?.response?.status !== 401) {
    return;
  }

  if (!isApiOrAuthRequest(error.config)) {
    return;
  }

  if (unauthorizedInFlight) {
    return unauthorizedInFlight;
  }

  unauthorizedInFlight = expireSessionAndRedirect();

  try {
    await unauthorizedInFlight;
  } finally {
    unauthorizedInFlight = null;
  }
}

async function expireSessionAndRedirect() {
  let hadSession = !!localStorage.getItem('indiefy-user');

  try {
    const { useAuthStore } = await import('@/store/auth');
    const authStore = useAuthStore();
    hadSession = hadSession || !!authStore.isAuthenticated;
    authStore.clearSession();
  } catch (error) {
    localStorage.removeItem('indiefy-user');
  }

  if (!hadSession) {
    return;
  }

  try {
    const router = (await import('@/router')).default;
    const current = router.currentRoute.value;

    if (AUTH_ROUTE_NAMES.has(current?.name)) {
      return;
    }

    const query = {};
    if (current?.fullPath && current.meta?.requiresAuth) {
      query.redirect = current.fullPath;
    }

    await router.replace({ name: 'login', query });
  } catch (error) {
    if (!window.location.pathname.includes('/auth/login')) {
      window.location.assign('/auth/login');
    }
  }
}
